import React, { useRef, FC } from 'react';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import { Modal, Form, FormItem, H1, Text } from '@erecruit/ui-kit';
import { Intl } from '@erecruit/core';
import { CustomFieldRenderer } from '@erecruit/modules';
import { Resources, Mutations } from '@erecruit/types';
import {
  MatchRecordSection,
  MatchRecordSections,
  SupportedMatchEditSection,
} from '../types';
import { hasValidationErrors } from '../../AddNewPositionWizard/helpers';
import { modalHeaders } from '../helpers';

export interface EditModalProps {
  visible: boolean;
  section: MatchRecordSection;
  sections: MatchRecordSections;
  onSave: (positionRecord: SupportedMatchEditSection) => void;
  onCancel: () => void;
}

const { FormattedMessage } = Intl;

export const EditMatchModal: FC<EditModalProps> = props => {
  const { visible, onCancel, onSave, section, sections } = props;
  const formRef = useRef<WrappedFormUtils>();

  let formFields = {};

  if (section === 'additionalInfo') {
    formFields = sections[section].map(field => {
      const { value, ...rendererProps } = field;
      return (
        <FormItem
          key={field.id}
          id={String(field.id)}
          label={field.name}
          hasFeedback
          options={{
            initialValue: value,
            rules: [
              {
                required: field.settings && field.settings.required,
                message: (
                  <FormattedMessage
                    id={messageIds.vms.errors.form.requiredField}
                  />
                ),
              },
            ],
          }}
        >
          <CustomFieldRenderer {...rendererProps} mode="edit" />
        </FormItem>
      );
    });
  }

  const handleSave = (section: MatchRecordSection) => {
    const form = formRef.current;
    if (!form) return;
    form.validateFields();

    if (!hasValidationErrors(form.getFieldsError())) {
      const values = form.getFieldsValue();
      let mutation: SupportedMatchEditSection | undefined;

      if (!values) return;

      if (section === 'additionalInfo') {
        const customFields: Mutations.CustomField[] = Object.keys(
          values as Resources.CustomField[],
        ).map(key => ({
          id: Number(key),
          value: JSON.stringify(values[key]),
        }));

        mutation = {
          additionalInfo: customFields,
        };
      }

      if (!mutation) return;
      onSave(mutation);
    }
  };

  return (
    <Modal
      pageTitle={<H1 color="headerTertiary">{modalHeaders[section].title}</H1>}
      title={
        <>
          <H1>{modalHeaders[section].title}</H1>
          <Text color="labelPrimary">{modalHeaders[section].description}</Text>
        </>
      }
      width={665}
      visible={visible}
      closable={true}
      onCancel={onCancel}
      onOk={() => handleSave(section)}
    >
      <Form
        onError={alert}
        onValidFieldsSubmit={alert}
        wrappedComponentRef={formRef}
      >
        {formFields}
      </Form>
    </Modal>
  );
};

export default EditMatchModal;
